import React, { useEffect, useState } from 'react';
import { formatDate } from '../utils/dateFormat';
import { authedFetch, API_BASE_URL } from '../services/api';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';

const ACTION_LABELS: Record<string, string> = {
  STATUS_CHANGED: 'Zmiana statusu',
  INSPECTION_CREATED: 'Utworzenie przeglądu',
  INSPECTION_UPDATED: 'Aktualizacja przeglądu',
  CEEB_SYNC_STARTED: 'Wysłanie do CEEB rozpoczęte',
  CEEB_SYNC_COMPLETED: 'Wysłanie do CEEB zakończone',
  CEEB_SYNC_FAILED: 'Błąd wysyłania do CEEB',
  RECORD_SAVED: 'Zapisano protokół lokalu',
  RECORD_UPDATED: 'Aktualizacja protokołu lokalu',
  APARTMENT_INSPECTION_CREATED: 'Utworzono inspekcję lokalu',
  APARTMENT_INSPECTION_UPDATED: 'Zaktualizowano inspekcję lokalu',
  HEAT_SOURCE_ADDED: 'Dodano źródło ciepła',
  HEAT_SOURCE_UPDATED: 'Zaktualizowano źródło ciepła',
  HEAT_SOURCE_REMOVED: 'Usunięto źródło ciepła',
  RECOMMENDATION_ADDED: 'Dodano zalecenie',
  SIGNATURE_SAVED: 'Zapisano podpis',
  ABSENCE_BULK_MARKED: 'Nieobecność oznaczona zbiorczo w panelu',
  AIR_VERSION_CONFLICT: 'Odrzucono zapis z nieaktualną wersją (konflikt wersji)',
};

const formatActionLabel = (action?: string): string => {
  if (!action) return 'Zmiana';
  return ACTION_LABELS[action] || action;
};

interface AuditLogEntry {
  id: string;
  inspectionId?: string;
  inspection_id?: string;
  apartmentId?: string;
  apartment_id?: string;
  action: string;
  payload?: any;
  createdAt?: string;
  created_at?: string;
  userName?: string;
  user_name?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  apartmentId: string | null;
  apartmentNumber?: string;
  buildingAddress?: string;
}

const renderPayloadDiff = (payload: any): React.ReactNode => {
  if (!payload || typeof payload !== 'object') return null;

  const lineStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-label)',
    color: 'var(--text-secondary)',
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };

  // Common diff shape: { from, to } or { field, from, to }
  if ('from' in payload || 'to' in payload) {
    const from = payload.from !== undefined ? String(payload.from) : '∅';
    const to = payload.to !== undefined ? String(payload.to) : '∅';
    return (
      <span style={lineStyle}>
        {payload.field ? <strong>{payload.field}: </strong> : null}
        {from} → {to}
      </span>
    );
  }

  // Otherwise, show as compact key: value list
  const entries = Object.entries(payload).filter(
    ([k]) => !['id', 'inspectionId', 'inspection_id', 'apartmentId', 'apartment_id'].includes(k)
  );
  if (entries.length === 0) return null;

  return (
    <span style={{ display: 'block' }}>
      {entries.slice(0, 6).map(([k, v]) => {
        if (v && typeof v === 'object' && ('from' in (v as any) || 'to' in (v as any))) {
          const vv = v as any;
          const from = vv.from !== undefined ? String(vv.from) : '∅';
          const to = vv.to !== undefined ? String(vv.to) : '∅';
          return (
            <span key={k} style={lineStyle}>
              <strong>{k}:</strong> {from} → {to}
            </span>
          );
        }
        const display =
          v === null || v === undefined
            ? '∅'
            : typeof v === 'object'
            ? JSON.stringify(v)
            : String(v);
        return (
          <span key={k} style={lineStyle}>
            <strong>{k}:</strong> {display.length > 120 ? display.slice(0, 120) + '…' : display}
          </span>
        );
      })}
    </span>
  );
};

export const ApartmentHistoryModal: React.FC<Props> = ({
  open,
  onClose,
  apartmentId,
  apartmentNumber,
  buildingAddress,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AuditLogEntry[]>([]);

  useEffect(() => {
    if (!open || !apartmentId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authedFetch(`${API_BASE_URL}/apartments/${apartmentId}/history`);
        if (!res.ok) {
          if (res.status === 404) {
            // Endpoint not implemented or no records — treat as empty
            if (!cancelled) setHistory([]);
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;
        const list: AuditLogEntry[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.history)
          ? data.history
          : Array.isArray(data?.entries)
          ? data.entries
          : [];
        setHistory(list);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Nie udało się pobrać historii');
          setHistory([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, apartmentId]);

  const title = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span style={{ fontSize: 'var(--text-h2)', fontWeight: 'var(--weight-bold)', color: 'var(--text-heading)' }}>
        Historia lokalu{apartmentNumber ? ` — lokal nr ${apartmentNumber}` : ''}
      </span>
      {buildingAddress ? (
        <span style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-regular)', color: 'var(--text-secondary)' }}>
          {buildingAddress}
        </span>
      ) : null}
    </div>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      width={720}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Zamknij
        </Button>
      }
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7) 0' }}>
          <Icon
            name="refresh"
            size={36}
            color="var(--action-primary)"
            title="Ładowanie"
            style={{ animation: 'ahmSpin 1s linear infinite' }}
          />
          <style>{`@keyframes ahmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            background: 'var(--status-deny-bg)',
            border: 'var(--border-width) solid var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--status-deny)',
            fontSize: 'var(--text-body)',
          }}
        >
          <Icon name="triangle-alert" size={24} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && history.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-7) 0',
            textAlign: 'center',
          }}
        >
          <Icon name="clipboard" size={40} color="var(--text-muted)" />
          <span style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>Brak historii</span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
            Nie odnotowano jeszcze żadnych zmian dla tego lokalu.
          </span>
        </div>
      )}

      {!loading && !error && history.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {history.map((entry) => {
            const when = entry.createdAt || entry.created_at;
            const user = entry.userName || entry.user_name;
            return (
              <div
                key={entry.id}
                style={{
                  padding: 'var(--space-4) 0',
                  borderBottom: 'var(--border-width) solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 12px',
                      fontSize: 'var(--text-label)',
                      fontWeight: 'var(--weight-semibold)',
                      lineHeight: 1,
                      color: 'var(--action-primary)',
                      background: 'var(--changed-bg)',
                      borderRadius: 'var(--radius-pill)',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {formatActionLabel(entry.action)}
                  </span>
                  <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(when)}
                  </span>
                  {user ? (
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>· {user}</span>
                  ) : null}
                </div>
                {renderPayloadDiff(entry.payload) ? (
                  <div style={{ marginTop: 'var(--space-2)' }}>{renderPayloadDiff(entry.payload)}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </ModalShell>
  );
};

export default ApartmentHistoryModal;
